import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { MasterSlot, ShopMaxPerDay, ShopMaxPerTime } from './../../shared/models/slot-management.model';
import produce from 'immer';
import * as fromActions from './sales-availability.actions';


export interface IAsyncData {
    loading: boolean;
    loaded: boolean;
    error: string;
}

export interface IMasterSlots extends IAsyncData {
    data: MasterSlot[];
}

export interface IShopMaxTotalDetails extends IAsyncData {
    data: ShopMaxPerDay[];
    isUpdated:boolean;
}

export interface IShopMaxSlotDetails extends IAsyncData {
    data: ShopMaxPerTime;
    isUpdated:boolean;
}

export interface ITransportTotalDetails extends IAsyncData {
    data: ShopMaxPerDay[];
    isUpdated:boolean;
}

export interface ITransportSlotDetails extends IAsyncData {
    data: ShopMaxPerTime;
    isUpdated:boolean;
}

export interface SalesAvailabilityState extends EntityState<any> {
    // additional entities state properties
    loading: boolean,
    loaded: boolean,
    error: string,
    masterSlots: IMasterSlots,
    shopMaxTotal: IShopMaxTotalDetails,
    shopMaxSlots: IShopMaxSlotDetails,
    remoteMaxTotal: ITransportTotalDetails,
    remoteMaxSlots: ITransportSlotDetails
}

export const adapter: EntityAdapter<any> = createEntityAdapter<any>();

export const initialState: SalesAvailabilityState = adapter.getInitialState({
    loading: false,
    loaded: false,
    error: '',
    masterSlots: {
        data: [],
        loading: false,
        loaded: false,
        error: ''
    },
    shopMaxTotal: {
        data: [],
        loading: false,
        loaded: false,
        error: '',
        isUpdated:true
    },
    shopMaxSlots: {
        data: {} as ShopMaxPerTime,
        loading: false,
        loaded: false,
        error: '',
        isUpdated:true
    },
    remoteMaxTotal: {
        data: [],
        loading: false,
        loaded: false,
        error: '',
        isUpdated:true
    },
    remoteMaxSlots: {
        data: {} as ShopMaxPerTime,
        loading: false,
        loaded: false,
        error: '',
        isUpdated:true
    }
});

export function salesAvailabilityReducer(state: SalesAvailabilityState = initialState,
    action: fromActions.SalesAvailabilityActions): SalesAvailabilityState {

    switch (action.type) {
        case fromActions.SalesAvailabilityActionTypes.LOAD_MASTER_SLOTS: {
            return produce(state, draftState => {
                draftState.masterSlots = {
                    data: [],
                    loading: true,
                    loaded: false,
                    error: ''
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_MASTER_SLOTS_SUCCESS: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.LoadMasterSlotsSuccess ? action.payload : undefined;
                draftState.masterSlots = {
                    data: payload.masterSlots,
                    loading: false,
                    loaded: true,
                    error: ''
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_MASTER_SLOTS_FAILURE: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.LoadMasterSlotsFailure ? action.payload : undefined;
                draftState.masterSlots = {
                    data: [],
                    loading: false,
                    loaded: true,
                    error: payload.error
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_SHOP_MAX_SLOTS: {
            return produce(state, draftState => {
                draftState.shopMaxSlots = {
                    data: {} as ShopMaxPerTime,
                    loading: true,
                    loaded: false,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_SHOP_MAX_SLOTS_SUCCESS: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.LoadShopMaxSlotsSuccess ? action.payload : undefined;
                draftState.shopMaxSlots = {
                    data: payload.shopMaxSlots,
                    loading: false,
                    loaded: true,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_SHOP_MAX_SLOTS_FAILURE: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.LoadShopMaxSlotsFailure ? action.payload : undefined;
                draftState.shopMaxSlots = {
                    data: {} as ShopMaxPerTime,
                    loading: false,
                    loaded: true,
                    error: payload.error,
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_SHOP_MAX_TOTAL: {
            return produce(state, draftState => {
                draftState.shopMaxTotal = {
                    data: [],
                    loading: true,
                    loaded: false,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_SHOP_MAX_TOTAL_SUCCESS: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.LoadShopMaxTotalSuccess ? action.payload : undefined;
                draftState.shopMaxTotal = {
                    data: payload.shopMaxTotal,
                    loading: false,
                    loaded: true,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_SHOP_MAX_TOTAL_FAILURE: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.LoadShopMaxTotalFailure ? action.payload : undefined;
                draftState.shopMaxTotal = {
                    data: [],
                    loading: false,
                    loaded: true,
                    error: payload.error,
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_SHOP_MAX_SLOTS: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateShopMaxSlots ? action.payload : undefined;
                draftState.shopMaxSlots = {
                   ...state.shopMaxSlots,
                   isUpdated:false
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_SHOP_MAX_SLOTS_SUCCESS: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateShopMaxSlotsSuccess ? action.payload : undefined;
                draftState.shopMaxSlots = {
                    data: payload.shopMaxSlots,
                    loading: false,
                    loaded: true,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_SHOP_MAX_SLOTS_FAILURE: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateShopMaxSlotsFailure ? action.payload : undefined;
                draftState.shopMaxSlots = {
                    data: {} as ShopMaxPerTime,
                    loading: false,
                    loaded: true,
                    error: payload.error,
                    isUpdated:false
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_SHOP_MAX_TOTAL: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateShopMaxTotal ? action.payload : undefined;
                draftState.shopMaxTotal = {
                    data: payload.shopMaxTotal,
                    loading: false,
                    loaded: true,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_SHOP_MAX_TOTAL_SUCCESS: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateShopMaxTotalSuccess ? action.payload : undefined;
                draftState.shopMaxTotal = {
                    data: payload.shopMaxTotal,
                    loading: false,
                    loaded: true,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_SHOP_MAX_TOTAL_FAILURE: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateShopMaxTotalFailure ? action.payload : undefined;
                draftState.shopMaxTotal = {
                    data: [],
                    loading: false,
                    loaded: true,
                    error: payload.error,
                    isUpdated:false
                }
            });
        }

        /* Transport Capacity */
        case fromActions.SalesAvailabilityActionTypes.LOAD_TRANSPORT_MAX_SLOTS: {
            return produce(state, draftState => {
                draftState.remoteMaxSlots = {
                    data: {} as ShopMaxPerTime,
                    loading: true,
                    loaded: false,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_TRANSPORT_MAX_SLOTS_SUCCESS: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.LoadTransportMaxSlotsSuccess ? action.payload : undefined;
                draftState.remoteMaxSlots = {
                    data: payload.transportMaxSlots,
                    loading: false,
                    loaded: true,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_TRANSPORT_MAX_SLOTS_FAILURE: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.LoadTransportMaxSlotsFailure ? action.payload : undefined;
                draftState.remoteMaxSlots = {
                    data: {} as ShopMaxPerTime,
                    loading: false,
                    loaded: true,
                    error: payload.error,
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_TRANSPORT_MAX_TOTAL: {
            return produce(state, draftState => {
                draftState.remoteMaxTotal = {
                    data: [],
                    loading: true,
                    loaded: false,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_TRANSPORT_MAX_TOTAL_SUCCESS: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.LoadTransportMaxTotalSuccess ? action.payload : undefined;
                draftState.remoteMaxTotal = {
                    data: payload.transportMaxTotal,
                    loading: false,
                    loaded: true,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.LOAD_TRANSPORT_MAX_TOTAL_FAILURE: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.LoadTransportMaxTotalFailure ? action.payload : undefined;
                draftState.remoteMaxTotal = {
                    data: [],
                    loading: false,
                    loaded: true,
                    error: payload.error,
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_TRANSPORT_MAX_SLOTS: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateTransportMaxSlots ? action.payload : undefined;
                draftState.remoteMaxSlots = {
                    ...state.remoteMaxSlots,
                    isUpdated:false
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_TRANSPORT_MAX_SLOTS_SUCCESS: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateTransportMaxSlotsSuccess ? action.payload : undefined;
                draftState.remoteMaxSlots = {
                    data: payload.transportMaxSlots,
                    loading: false,
                    loaded: true,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_TRANSPORT_MAX_SLOTS_FAILURE: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateTransportMaxSlotsFailure ? action.payload : undefined;
                draftState.remoteMaxSlots = {
                    data: {} as ShopMaxPerTime,
                    loading: false,
                    loaded: true,
                    error: payload.error,
                    isUpdated:false
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_TRANSPORT_MAX_TOTAL: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateTransportMaxTotal ? action.payload : undefined;
                draftState.remoteMaxTotal = {
                    data: payload.transportMaxTotal,
                    loading: false,
                    loaded: true,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_TRANSPORT_MAX_TOTAL_SUCCESS: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateTransportMaxTotalSuccess ? action.payload : undefined;
                draftState.remoteMaxTotal = {
                    data: payload.transportMaxTotal,
                    loading: false,
                    loaded: true,
                    error: '',
                    isUpdated:true
                }
            });
        }
        case fromActions.SalesAvailabilityActionTypes.UPDATE_TRANSPORT_MAX_TOTAL_FAILURE: {
            return produce(state, draftState => {
                const payload = action instanceof fromActions.UpdateTransportMaxTotalFailure ? action.payload : undefined;
                draftState.remoteMaxTotal = {
                    data: [],
                    loading: false,
                    loaded: true,
                    error: payload.error,
                    isUpdated:false
                }
            });
        }
        default:
            return state;
    }
}